import { useMemo, useState } from "react";
import { EllipsisHorizontalCircleIcon } from "@heroicons/react/24/outline";

    // Convierte una key del objeto en un texto legible para usar como título de columna, EJEMPLO: "minimumStock" => "Minimum Stock"
    const formatLabel = (key) => {
        return key
            .replace(/([A-Z])/g, " $1")
            .replace(/_/g, " ")
            .replace(/^./, (char) => char.toUpperCase());
        };

        // OBTIENE UN IDENTIFICADOR UNICO PARA CADA FILA, PUEDE SER "id", "_id" O EL ÍNDICE DE LA FILA
        const getRowId = (row, index) => {
        return row.id || row._id || index;
        };

        // PROPS PRINCIPALES DE LA TABLA. TODAS SON OPCIONALES PARA QUE EL COMPONENTE SEA REUTILIZABLE
        const ReusableTable = ({
        columns,
        data = [],
        loading = false,
        emptyMessage = "No hay datos para mostrar",
        selectable = false,
        actions = [],
        onRowClick,
        onHeaderClick,
        selectedRowId = null,
        rowClassName,
        }) => {
        // GUARDA LOS IDS DE LAS FILAS SELECCIONADAS.
        const [selectedRows, setSelectedRows] = useState([]);
        
        // GUARDA QUE MENU DE ACCIONES ESTA ABIERTO.
        const [openMenuId, setOpenMenuId] = useState(null);

        // GUARDA LA POSICION DEL MENU FLOTANTE DE ACCIONES.
        const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });


        // Define las columnas finales de la tabla.
        // Si vienen columnas por props, usa esas.
        // Si no vienen, las genera automáticamente desde las keys del primer objeto de data.
        const tableColumns = useMemo(() => {
            if (columns && columns.length > 0) return columns;

            if (!data || data.length === 0) return [];

            return Object.keys(data[0])
            .filter((key) => key !== "id" && key !== "_id")
            .map((key) => ({
                key,
                label: formatLabel(key),
            }));
        }, [columns, data]);

        // Indica si hay acciones disponibles para mostrar la columna de acciones.
        const hasActions = actions && actions.length > 0;

        
        // Indica si todas las filas están seleccionadas.
        const allSelected =
            data.length > 0 && selectedRows.length === data.length;


        // Selecciona o deselecciona todas las filas desde el checkbox del header.
        const handleSelectAll = () => {
            if (allSelected) {
            setSelectedRows([]);
            return;
            }

            const allRowIds = data.map((row, index) => getRowId(row, index));
            setSelectedRows(allRowIds);
        };


        // Selecciona o deselecciona una fila individual.
        const handleSelectRow = (row, index) => {
            const rowId = getRowId(row, index);

            setSelectedRows((prevSelectedRows) => {
            if (prevSelectedRows.includes(rowId)) {
                return prevSelectedRows.filter((id) => id !== rowId);
            }

            return [...prevSelectedRows, rowId];
            });
        };


        // Renderiza el contenido de cada celda.
        // Si la columna tiene render personalizado, lo usa.
        // Si no, muestra el valor de la key correspondiente.
        // Si no hay valor, muestra "-".
        const renderCellValue = (column, row, rowIndex) => {
            if (column.render) {
            return column.render(row, rowIndex);
            }

            const value = row[column.key];

            if (value === null || value === undefined || value === "") {
            return "-";
            }

            return value;
        };


        // Calcula el total de columnas para que el mensaje vacío ocupe todo el ancho de la tabla.
        const totalColumns =
            tableColumns.length + (selectable ? 1 : 0) + (hasActions ? 1 : 0);



            // Abre o cierra el menú de acciones.
            // Calcula la posición del botón para mostrar el dropdown flotante sin generar scroll dentro de la tabla.
            const handleOpenMenu = (event, rowId) => {
                event.stopPropagation();

                const buttonRect = event.currentTarget.getBoundingClientRect();

                if (openMenuId === rowId) {
                    setOpenMenuId(null);
                    return;
                }

                setMenuPosition({
                    top: buttonRect.bottom + 6,
                    left: buttonRect.right - 160,
                });

                setOpenMenuId(rowId);
            };

            
            const getExternalRowClassName = (row, rowIndex) => {
                if(typeof rowClassName === "function") {
                    return rowClassName(row, rowIndex);
                }

                return rowClassName || "";
            };


        return (
        <div className="tw-w-full tw-overflow-hidden tw-rounded-md tw-border tw-border-gray-200 tw-bg-white">
            <div className="tw-w-full tw-overflow-x-auto tw-rounded-md">
                <table className="tw-w-full tw-min-w-full tw-border-separate tw-border-spacing-0 tw-text-sm tw-[--bs-table-bg:#F5F5F5] tw-[--bs-table-accent-bg:transparent]">
                <thead 
                    onClick={onHeaderClick}
                    className="!tw-bg-[#F5F5F5] tw-[--bs-table-bg:#F5F5F5] tw-[--bs-table-accent-bg:transparent] !tw-text-gray-900 tw-[--bs-table-color:#111827]">
                    <tr className="tw-border-b tw-border-gray-200">
                    {selectable && (
                        <th className="!tw-bg-[#F5F5F5] !tw-text-gray-900 tw-[--bs-table-color:#111827] tw-[--bs-table-bg:#F5F5F5] tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide">
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={handleSelectAll}
                            disabled={loading || data.length === 0}
                            className="tw-h-4 tw-w-4 tw-cursor-pointer tw-rounded tw-border-gray-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                        />
                        </th>
                    )}


                    {/* // Renderiza dinámicamente los títulos de las columnas. */}
                    {tableColumns.map((column) => (
                        <th
                        key={column.key}
                        className="!tw-bg-[#F5F5F5] tw-[--bs-table-bg:#F5F5F5] tw-[--bs-table-accent-bg:transparent] tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide !tw-text-gray-900"
                        >
                        {column.label}
                        </th>
                    ))}

                    {/* // Si existen acciones, agrega una columna final para el menú de acciones. */}
                    {hasActions && (
                        <th className="tw-w-12 !tw-bg-[#F5F5F5] tw-[--bs-table-bg:#F5F5F5] tw-[--bs-table-accent-bg:transparent] tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-600">
                        Acciones
                        </th>
                    )}
                    </tr>
                </thead>

                <tbody>
                    {loading &&
                    Array.from({ length: 6 }).map((_, rowIndex) => (
                        <tr
                        key={`skeleton-${rowIndex}`}
                        className="tw-border-b tw-border-gray-100 last:tw-border-b-0"
                        >
                        {selectable && (
                            <td className="tw-px-4 tw-py-4">
                            <div className="tw-h-4 tw-w-4 tw-animate-pulse tw-rounded tw-bg-gray-200" />
                            </td>
                        )}

                        {tableColumns.map((column) => (
                            <td key={column.key} className="tw-px-4 tw-py-4">
                            <div className="tw-h-4 tw-w-24 tw-animate-pulse tw-rounded tw-bg-gray-200" />
                            </td>
                        ))}

                        {hasActions && (
                            <td className="tw-px-4 tw-py-4">
                            <div className="ml-auto tw-h-4 tw-w-6 tw-animate-pulse tw-rounded tw-bg-gray-200" />
                            </td>
                        )}
                        </tr>
                    ))}

                    {!loading && data.length === 0 && (
                    <tr>
                        <td
                        colSpan={totalColumns || 1}
                        className="tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-gray-500"
                        >
                        {emptyMessage}
                        </td>
                    </tr>
                    )}

                    {!loading &&
                    data.length > 0 &&
                    data.map((row, rowIndex) => {
                        const rowId = getRowId(row, rowIndex);
                        const isSelected = selectedRows.includes(rowId);
                        const isExternallySelected = 
                            selectedRowId !== null &&
                            selectedRowId !== undefined &&
                            String(selectedRowId) === String(rowId);
                        const externalRowClassName = getExternalRowClassName(
                            row,
                            rowIndex
                        );

                        return (
                        <tr
                            key={rowId}
                            onClick={( ) => {
                                if (onRowClick) {
                                    onRowClick(row, rowIndex);
                                }
                            }}
                            className={`tw-border-b tw-border-gray-100 tw-transition-colors last:tw-border-b-0 hover:tw-bg-gray-50 ${onRowClick ? "tw-cursor-pointer" : ""}
                            ${isExternallySelected ? "selected-row tw-bg-gray-100" : ""}
                            ${externalRowClassName || ""}`}
                        >

                            {selectable && (
                            <td className="tw-px-4 tw-py-4">
                                <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(event) => {
                                    event.stopPropagation();
                                    handleSelectRow(row, rowIndex);
                                }}
                                className="tw-h-4 tw-w-4 tw-cursor-pointer tw-rounded tw-border-gray-300"
                                />
                            </td>
                            )}

                            {tableColumns.map((column) => (
                            <td
                                key={column.key}
                                className="tw-whitespace-nowrap tw-px-4 tw-py-4 tw-text-sm tw-text-gray-700"
                            >
                                {renderCellValue(column, row, rowIndex)}
                            </td>
                            ))}

                            {hasActions && (
                            <td className="tw-relative tw-px-4 tw-py-4 tw-text-right">
                                <button
                                type="button"
                                onClick={(event) => handleOpenMenu(event, rowId)}
                                className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-gray-500 tw-transition hover:tw-bg-gray-100 hover:tw-text-gray-800"
                                >
                                <EllipsisHorizontalIcon className="tw-h-5 tw-w-5"/>
                                </button>

                                {openMenuId === rowId && (
                                    <div
                                        style={{
                                        top: `${menuPosition.top}px`,
                                        left: `${menuPosition.left}px`,
                                        }}
                                        className="tw-fixed tw-z-[9999] tw-w-40 tw-overflow-hidden tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
                                    >
                                        {actions.map((action) => (
                                        <button
                                            key={action.label}
                                            type="button"
                                            onClick={() => {
                                            action.onClick(row, rowIndex);
                                            setOpenMenuId(null);
                                            }}
                                            className={`tw-block tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-transition hover:tw-bg-gray-50 ${
                                            action.variant === "danger"
                                                ? "tw-text-red-600"
                                                : "tw-text-gray-700"
                                            }`}
                                        >
                                            {action.label}
                                        </button>
                                        ))}
                                    </div>
                                    )}
                            </td>
                                )}
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ReusableTable;